import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import Login from "./pages/login/index.jsx";
import LandingPage from "./pages/landing/index.jsx";
import { Error400, Error404 } from "./pages/error";
import { getLoggedInUser, isAuthenticated, isMockModeEnabled } from "./utils/common";
import LayoutView from "./components/LayoutView.jsx";
import routers from "./routers";
import Cookies from "js-cookie";
import { TooltipProvider } from "./components/ui/tooltip";
import ChunkLoadErrorBoundary from "./components/ChunkLoadErrorBoundary";

const RouteLoadingFallback = () => (
  <div className="p-6 text-muted-foreground">Loading...</div>
);

const readCachedUser = () => {
  try {
    const raw = window.localStorage.getItem("userInfo");
    if (!raw) return null;

    const cached = JSON.parse(raw);
    return cached && typeof cached === "object" ? cached : null;
  } catch (e) {
    return null;
  }
};

const normalizePermissions = (nextUser) =>
  Array.isArray(nextUser?.permissions) ? nextUser.permissions : [];

const hasHydratedPermissions = (nextUser) =>
  Array.isArray(nextUser?.permissions) || Array.isArray(nextUser?.permission);

const ProtectedRoute = ({
  Component,
  Roles = [],
  permissions,
  permissionsMode,
  authReady,
  userPermissions = [],
  ...rest
}) => (
  <Route
    {...rest}
    render={(props) => {
      if (isAuthenticated()) {
        if (!authReady && userPermissions.length === 0) {
          return (
            <div className="min-h-screen bg-background flex items-center justify-center">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          );
        }

        const normalizedPermissions = Array.isArray(permissions) ? permissions : [];
        const mode = permissionsMode === "all" ? "all" : "any";

        if (normalizedPermissions.length > 0) {
          const userPerms = Array.isArray(userPermissions) ? userPermissions : [];
          const hasAccess =
            mode === "all"
              ? normalizedPermissions.every((p) => userPerms.includes(p))
              : normalizedPermissions.some((p) => userPerms.includes(p));

          if (!hasAccess) {
            return (
              <Redirect
                to={{ pathname: "/error400", state: { from: props.location } }}
              />
            );
          }
        } else {
          // Backward compatible: keep legacy roleId-based guard for routes not migrated to permissions yet.
          const role = Number(Cookies.get("roles"));
          if (!Roles.includes(Number(role))) {
            return (
              <Redirect
                to={{ pathname: "/error400", state: { from: props.location } }}
              />
            );
          }
        }

        return (
          <ChunkLoadErrorBoundary>
            <React.Suspense fallback={<RouteLoadingFallback />}>
              <Component {...props} />
            </React.Suspense>
          </ChunkLoadErrorBoundary>
        );
      } else {
        return (
          <Redirect
            to={{ pathname: "/login", state: { from: props.location } }}
          />
        );
      }
    }}
  />
);

function App() {
  const [user, setUser] = useState(() => readCachedUser() || {});
  const [userPermissions, setUserPermissions] = useState(() =>
    normalizePermissions(readCachedUser())
  );
  const [authReady, setAuthReady] = useState(() => !isAuthenticated());

  useEffect(() => {
    let isMounted = true;

    const syncAuthState = (nextUser, { ready = true } = {}) => {
      if (!isMounted) {
        return;
      }

      setUser(nextUser || {});
      setUserPermissions(normalizePermissions(nextUser));
      setAuthReady(ready);
    };

    const initAuth = async () => {
      if (!isAuthenticated()) {
        syncAuthState({}, { ready: true });
        return;
      }

      const cachedUser = readCachedUser();
      if (cachedUser) {
        syncAuthState(cachedUser, { ready: hasHydratedPermissions(cachedUser) });
      } else {
        syncAuthState({}, { ready: false });
      }

      try {
        // Revalidate user/RBAC in the background while the shell is already rendered from cache.
        const refreshedUser = await getLoggedInUser({ force: true });
        const hasRefreshedIdentity =
          refreshedUser &&
          typeof refreshedUser === "object" &&
          (refreshedUser.id || refreshedUser.email || refreshedUser.username);

        if (!hasRefreshedIdentity) {
          syncAuthState({}, { ready: true });
          return;
        }

        syncAuthState(refreshedUser, { ready: true });
      } catch (error) {
        syncAuthState({}, { ready: true });
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleUserInfoUpdated = (event) => {
      // If auth changed and userInfo was refreshed, rehydrate RBAC permissions.
      // This avoids permission checks running against an empty/stale permissions array after login.
      if (!isAuthenticated()) return;

      let nextUser = event?.detail?.user;
      if (!nextUser) {
        nextUser = readCachedUser() || {};
      }

      setUser(nextUser || {});
      setUserPermissions(normalizePermissions(nextUser));
      setAuthReady(true);
    };

    window.addEventListener("tna:userInfo:updated", handleUserInfoUpdated);

    return () => {
      window.removeEventListener("tna:userInfo:updated", handleUserInfoUpdated);
    };
  }, []);

  useEffect(() => {
    const handleAuthLogout = () => {
      // Keep UI state consistent after logout/session expiry without requiring a full reload.
      setUser({});
      setUserPermissions([]);
      setAuthReady(true);
    };

    window.addEventListener("tna:auth:logout", handleAuthLogout);

    return () => {
      window.removeEventListener("tna:auth:logout", handleAuthLogout);
    };
  }, []);
  return (
    <TooltipProvider>
      <Router>
        <Switch>
          <Route exact path="/login" component={Login} />
          <Route exact path="/welcome" component={LandingPage} />
          <Route
            path="/"
            render={(routeProps) => {
              const isMockDataEnabled = isMockModeEnabled(routeProps?.location?.search);

              if (!isAuthenticated() && !isMockDataEnabled) {
                return <Redirect to={{ pathname: "/login" }} />;
              }

              return (
                <div className="min-h-screen bg-background">
                  <div id="backdrop"></div>
                  <LayoutView user={user}>
                    <Switch>
                      {routers.map((route) => (
                        <ProtectedRoute
                          exact
                          key={String(route.path)}
                          path={route.path}
                          Component={route.component}
                          Roles={route.roles}
                          permissions={route.permissions}
                          permissionsMode={route.permissionsMode}
                          authReady={authReady}
                          userPermissions={userPermissions}
                        />
                      ))}
                      <Route exact path="/error400" component={Error400} />
                      <Route path="*" component={Error404} />
                    </Switch>
                  </LayoutView>
                </div>
              );
            }}
          />
        </Switch>
      </Router>
    </TooltipProvider>
  );
}

export default App;
